import { useMemo, useEffect, useState } from 'react'
import { Stack, Button, Box, Typography, Select, MenuItem, FormControl, InputLabel } from '@mui/material'
import CommissionTable from './commission-table/commission-table'
import { useGetCommissionsQuery, useMarkCommissionsAsPaidMutation, useGetAvailableWeeksQuery } from 'store/commissions/commissions.api'
import { getUserId, getAccessToken } from 'utils/common'
import SubmitLockButton from 'components/submit-lock-button/submit-lock-button.component'

interface CommissionDetailsProps {
  tabsValue: number
  selectedWeek: number | undefined
  onWeekChange: (week: number | undefined) => void
}

const CommissionDetails = ({ tabsValue, selectedWeek, onWeekChange }: CommissionDetailsProps) => {
  const userId = getUserId()
  const status = tabsValue === 0 ? 'NOT_PAID' : 'PAID'
  const [markAsPaid] = useMarkCommissionsAsPaidMutation()
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false)

  const { data: availableWeeks } = useGetAvailableWeeksQuery(
    { userId: userId || '', status },
    { skip: !userId }
  )

  // Set largest week as default when availableWeeks loads
  useEffect(() => {
    if (availableWeeks && availableWeeks.length > 0 && selectedWeek === undefined) {
      const largestWeek = Math.max(...availableWeeks)
      onWeekChange(largestWeek)
    }
  }, [availableWeeks, selectedWeek, onWeekChange])

  const { data, refetch } = useGetCommissionsQuery(
    {
      userId: userId || '',
      week: selectedWeek,
      status,
    },
    { skip: !userId || selectedWeek === undefined }
  )

  const commissions = useMemo(() => {
    return data?.commissions || []
  }, [data?.commissions])

  // Calculate total commission for the week
  const totalCommission = useMemo(() => {
    return commissions.reduce((sum, commission) => sum + commission.commission, 0)
  }, [commissions])

  const handleMarkAsPaid = async () => {
    if (selectedWeek !== undefined && userId) {
      try {
        await markAsPaid({ userId, week: selectedWeek }).unwrap()
        // Refetch to update the list
        await refetch()
      } catch (error) {
        console.error('Failed to mark commissions as paid:', error)
      }
    }
  }

  const handlePrint = async () => {
    if (!userId || selectedWeek === undefined) {
      return
    }

    setIsGeneratingPdf(true)
    try {
      const baseUrl = process.env.REACT_APP_ONBOARDING_SERVICE_API
      const accessToken = getAccessToken()
      
      let url = `${baseUrl}/oapi/v1/commissions/${userId}/generate-pdf`
      const params = new URLSearchParams()
      if (selectedWeek !== undefined) {
        params.append('week', selectedWeek.toString())
      }
      if (status) {
        params.append('status', status)
      }
      const queryString = params.toString()
      if (queryString) {
        url += `?${queryString}`
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'userId': userId,
        },
      })

      if (!response.ok) {
        throw new Error('Failed to generate PDF')
      }

      const blob = await response.blob()

      // Create a blob URL and open it in a new tab with PDF viewer
      const blobUrl = URL.createObjectURL(blob)
      
      // Open in new tab - browser will show PDF viewer with download option
      const newWindow = window.open(blobUrl, '_blank')
      
      if (newWindow) {
        // Ensure the window focuses on the PDF
        newWindow.focus()
        // Clean up the blob URL after a longer delay to ensure PDF loads
        setTimeout(() => {
          URL.revokeObjectURL(blobUrl)
        }, 5000)
      } else {
        // If popup was blocked, try alternative method
        // Create a temporary link and click it to open in new tab
        const link = document.createElement('a')
        link.href = blobUrl
        link.target = '_blank'
        link.rel = 'noopener noreferrer'
        // Don't set download attribute - this allows browser to open PDF viewer
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        // Clean up after delay
        setTimeout(() => {
          URL.revokeObjectURL(blobUrl)
        }, 5000)
      }
    } catch (error) {
      console.error('Failed to generate commission PDF:', error)
    } finally {
      setIsGeneratingPdf(false)
    }
  }

  return (
    <Stack sx={{ height: '100%', justifyContent: 'space-between' }}>
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <Box sx={{ p: 2, bgcolor: 'secondary.light' }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Week#</InputLabel>
            <Select
              value={selectedWeek || ''}
              label="Week#"
              onChange={(e) => onWeekChange(e.target.value as number | undefined)}
            >
              {availableWeeks?.map((week) => (
                <MenuItem key={week} value={week}>
                  Week {week}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <CommissionTable commissions={commissions} />
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Box>
          {tabsValue === 0 && (
            <SubmitLockButton
              variant="contained"
              color="primary"
              onClick={handleMarkAsPaid}
              disabled={commissions.length === 0}
            >
              Mark as Paid
            </SubmitLockButton>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            Total Commission: {commissions[0]?.currency || 'CAD'} {totalCommission.toFixed(2)}
          </Typography>
          <Button variant="outlined" onClick={handlePrint} disabled={isGeneratingPdf || commissions.length === 0}>
            {isGeneratingPdf ? 'Generating...' : 'Print'}
          </Button>
        </Box>
      </Box>
    </Stack>
  )
}

export default CommissionDetails

